import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Loader2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState(token ? 'pending' : 'no_token');
  const [errorMsg, setErrorMsg] = useState('');

  // Resend form state — only used in the error/no_token branch.
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | error
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/auth/verify-email`, { params: { token } });
        if (!cancelled) setStatus('success');
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(err.response?.data?.detail || 'This verification link is invalid or has expired.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    const email = resendEmail.trim().toLowerCase();
    if (!email) return;
    setResendState('sending');
    setResendError('');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResendState('sent');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setResendError('Too many requests. Please wait a minute, then try again.');
      } else {
        setResendError(err.response?.data?.detail || 'Could not send a new link. Try again in a moment.');
      }
      setResendState('error');
    }
  };

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16 flex items-center">
      <div className="max-w-md mx-auto w-full px-6">
        <div className="mb-6">
          <span className="enf-brand-pill inline-flex items-center">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-12 w-auto"
              loading="eager"
            />
          </span>
        </div>

        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Email confirmation</div>

        {status === 'pending' && (
          <div className="px-4 py-6 bg-enf-surface-deep border border-enf-surface-border rounded-md flex items-center gap-3" data-testid="verify-email-pending">
            <Loader2 className="w-5 h-5 text-enf-role-info animate-spin" />
            <span className="text-sm text-enf-text-secondary">Confirming your email&hellip;</span>
          </div>
        )}

        {status === 'success' && (
          <>
            <h1 className="font-display text-5xl tracking-wide leading-none mb-3">You&apos;re in.</h1>
            <p className="text-enf-text-secondary mb-6 text-sm">Email confirmed. Your account is fully active.</p>
            <div className="px-4 py-5 bg-emerald-950/40 border border-emerald-900/60 rounded-md mb-6" data-testid="verify-email-success">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <div className="text-sm text-enf-text-secondary leading-relaxed">
                  We can now reach you for password resets, security notices, and important account updates.
                </div>
              </div>
            </div>
            <Link
              to="/progress"
              className="inline-block px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors"
              data-testid="verify-email-cta-progress"
            >
              Open the app
            </Link>
          </>
        )}

        {(status === 'error' || status === 'no_token') && (
          <>
            <h1 className="font-display text-5xl tracking-wide leading-none mb-3">Link expired.</h1>
            <p className="text-enf-text-secondary mb-6 text-sm">Verification links are good for 24 hours. No worries — pop your email in below and we&apos;ll send a fresh one.</p>
            <div className="px-4 py-5 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md mb-6" data-testid="verify-email-error">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-enf-accent-fg mt-0.5 shrink-0" />
                <div className="text-sm text-enf-text-secondary leading-relaxed">
                  {errorMsg || 'No verification token was provided.'}
                </div>
              </div>
            </div>

            {resendState !== 'sent' && (
              <form onSubmit={handleResend} className="mb-6 space-y-3" data-testid="verify-email-resend-form">
                <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted" htmlFor="resend-email">
                  Your email address
                </label>
                <input
                  id="resend-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-sm text-enf-text-primary placeholder:text-enf-text-muted focus:outline-none focus:border-enf-role-info"
                  data-testid="verify-email-resend-input"
                />
                <button
                  type="submit"
                  disabled={resendState === 'sending' || !resendEmail.trim()}
                  className="w-full px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover disabled:opacity-50 disabled:cursor-not-allowed rounded-md font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="verify-email-resend-button"
                >
                  {resendState === 'sending' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending&hellip;</>
                  ) : (
                    <><Mail className="w-4 h-4" /> Send me a new link</>
                  )}
                </button>
                {resendError && (
                  <div className="text-xs text-enf-accent-fg" data-testid="verify-email-resend-error">{resendError}</div>
                )}
              </form>
            )}

            {resendState === 'sent' && (
              <div className="px-4 py-5 bg-emerald-950/40 border border-emerald-900/60 rounded-md mb-6" data-testid="verify-email-resend-sent">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-enf-text-secondary leading-relaxed">
                    If <span className="text-enf-text-primary font-semibold">{resendEmail}</span> matches an Enferno account that needs confirmation, a fresh link is on its way.
                    <div className="mt-2 text-enf-text-muted text-xs">
                      Heads up: first-time emails from us sometimes land in spam or promotions. If you don&apos;t see it in your inbox within a minute or two, check those folders.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-sm text-enf-text-muted">
              Already confirmed?{' '}
              <Link to="/login" className="text-enf-role-info hover:text-enf-text-primary font-semibold">Sign in</Link>.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
